/**
 * Dashboard Exports Store
 *
 * Manages auto-export scheduling and dashboard download functionality.
 * Extracted from dashboards.js for better separation of concerns.
 */

import capitalize from 'lodash/capitalize';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { DashboardAPI } from '@/apis';
import { useDashboardTrackingProps } from '@/app/dashboards/composables/useDashboardTrackingProps';
import { DashboardUserEventTracker } from '@/app/dashboards/mixpanel';
import { buildFilterOverrideParams } from '@/app/dashboards/utils/report.utils';
import { originForReport } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { usePdfStore } from '@/stores/pdf';
import { useTrackingStore } from '@/stores/tracking';
import type { Brand, Dashboard, Identity, Report } from '@/types/auth.types';
import { downloadFileFromMemory } from '@/utils';
import { stringifyQuery } from '@/utils/query';

// ============================================================================
// Types
// ============================================================================

export type AutoExportFormMode = 'CREATE' | 'EDIT' | null;

export interface AutoExport {
  id?: number;
  dashboard_id?: number;
  dashboard?: {
    id: number;
    name: string;
    user_role?: string;
  };
  format?: string;
  frequency?: string;
  recipients?: unknown[];
  [key: string]: unknown;
}

/**
 * Type for the parts of dashboardsStore we access.
 * This avoids circular dependency issues with importing the actual store type.
 */
interface DashboardsStoreSlice {
  currentDashboard: Dashboard | null;
  currentDashboardId: number | undefined;
  currentDashboardName: string | undefined;
  currentDashboardRole: string | undefined;
  reports: Record<string, Report>;
  industries: unknown[];
  dashboardIdentityBrands: Brand[];
  dashboardCustomizeMode: boolean;
}

/**
 * Type for the parts of authStore we access.
 */
interface AuthStoreSlice {
  identity: Identity | null;
  currentBrand: Brand | null;
}

// Re-export types that other files may need
export type { Dashboard, Report };

export interface DownloadPDFOptions {
  path: string;
  socketId: string;
  useWaitFor?: boolean;
  topics?: unknown[];
  comparisons?: unknown[];
}

export interface DownloadSpreadsheetOptions {
  socketId: string;
  mixpanelPayload?: {
    topics?: unknown[];
    comparisons?: unknown[];
  };
}

export interface DownloadReportCSVOptions {
  report: Report;
  messageTagFilterOptions?: unknown;
}

export interface DownloadReportDataCSVOptions {
  brandIds: string;
  startDate: string;
  endDate: string;
  contextStartDate: string;
  contextEndDate: string;
  channels: string;
  metrics: string;
  reportType: string;
  sortOrder?: string;
  limit?: number;
  timeScale?: string;
  filenamePrefix?: string;
  csvLayout?: string;
}

// ============================================================================
// Store
// ============================================================================

const mixpanelTracker = new DashboardUserEventTracker();

export const useDashboardExportsStore = defineStore('dashboardExports', () => {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const _currentDashboardAutoExports = ref<AutoExport[]>([]);
  const allDashboardAutoExports = ref<AutoExport[]>([]);
  const autoExportFormShow = ref(false);
  const autoExportFormMode = ref<AutoExportFormMode>(null);
  const autoExportFormTarget = ref<AutoExport | null>(null);
  const autoExportListShow = ref(false);
  const spreadsheetExportDownloading = ref(false);
  const reportDataCsvLoading = ref(false);

  // -------------------------------------------------------------------------
  // Getters
  // -------------------------------------------------------------------------

  // NOTE: This getter needs to access dashboardsStore lazily to avoid circular dependency.
  // The dashboards.js store imports this store, so we can't import it at module level.
  function getCurrentDashboardAutoExports(): AutoExport[] {
    // Lazy import to avoid circular dependency
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const currentDashboard = dashboardsStore.currentDashboard;
    return _currentDashboardAutoExports.value.map((autoExport) => {
      if (currentDashboard && autoExport.dashboard_id === currentDashboard.id) {
        // Add dashboard object to be used in common auto export list component
        return {
          ...autoExport,
          dashboard: {
            id: currentDashboard.id,
            name: currentDashboard.name ?? '',
            user_role: dashboardsStore.currentDashboardRole ?? '',
          },
        };
      }
      return autoExport;
    });
  }

  const currentDashboardAutoExports = computed(() => getCurrentDashboardAutoExports());

  // -------------------------------------------------------------------------
  // Actions - Form Management
  // -------------------------------------------------------------------------

  function launchScheduleAutoExportForm(autoExport: AutoExport | null = null) {
    autoExportListShow.value = false;
    autoExportFormShow.value = true;
    autoExportFormMode.value = autoExport ? 'EDIT' : 'CREATE';
    autoExportFormTarget.value = autoExport;
  }

  function closeScheduleAutoExportForm() {
    autoExportFormShow.value = false;
    autoExportFormMode.value = null;
    autoExportFormTarget.value = null;
  }

  function launchAutoExportList() {
    autoExportListShow.value = true;
    autoExportFormShow.value = false;
    autoExportFormMode.value = null;
    autoExportFormTarget.value = null;
  }

  function closeAutoExportList() {
    autoExportListShow.value = false;
  }

  // -------------------------------------------------------------------------
  // Actions - State Updates
  // -------------------------------------------------------------------------

  function setDownloadingSpreadsheetExport(downloading: boolean) {
    spreadsheetExportDownloading.value = downloading;
  }

  function updateAllDashboardAutoExports(autoExports: AutoExport[]) {
    allDashboardAutoExports.value = Array.isArray(autoExports) ? autoExports : [];
  }

  function updateCurrentDashboardAutoExports(autoExports: AutoExport[]) {
    _currentDashboardAutoExports.value = Array.isArray(autoExports) ? autoExports : [];
  }

  function clearCurrentDashboardAutoExports() {
    _currentDashboardAutoExports.value = [];
  }

  function setReportDataCsvLoading(loading: boolean) {
    reportDataCsvLoading.value = loading;
  }

  // -------------------------------------------------------------------------
  // Actions - Downloads
  // -------------------------------------------------------------------------

  async function downloadDashboardPDF({
    path,
    socketId,
    useWaitFor,
    topics,
    comparisons,
  }: DownloadPDFOptions) {
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const dashboardReportsStore = useDashboardReportsStore();
    const authStore = useAuthStore() as unknown as AuthStoreSlice;
    const pdfStore = usePdfStore();

    const currentDashboard = dashboardsStore.currentDashboard;
    const userId = authStore.identity?.id ?? 'unknown';
    const currentBrand = authStore.currentBrand;
    const dashboardId = currentDashboard?.id ?? 'unknown';
    const dashboardName = currentDashboard?.name ?? 'dashboard';
    const dashboardLabel = currentDashboard?.label ?? 'dashboard';
    const startDate = dashboardReportsStore.reportDateRange?.[0];
    const endDate = dashboardReportsStore.reportDateRange?.[1];
    const filename = `${dashboardLabel}-${startDate}-to-${endDate}`;
    const reports = dashboardsStore.reports;

    const reportsIdAndType = Object.entries(reports).map(([, report]) => ({
      report_id: report.id,
      report_type: report.type,
    }));
    const type = 'dashboard';
    const url = `${originForReport()}${path}/report?${stringifyQuery({
      ...dashboardReportsStore.reportPageQueryParams,
      no_cover_page: true,
      brand: currentBrand?.label,
    })}`;

    void pdfStore.getPdfV2({
      filename,
      notificationSocketId: socketId,
      idleMaxInflightRequest: 2,
      idleTimeout: 10,
      notificationMeta: JSON.stringify({
        type,
        dashboardId,
        dashboardName,
        userId,
      }),
      height: '1150px',
      width: '1550px',
      url,
      landscape: true,
      useWaitFor: useWaitFor || false,
      margin: 20,
      format: undefined,
      scale: undefined,
    });

    const { dashboardTrackingProps } = useDashboardTrackingProps({
      dashboard: dashboardsStore.currentDashboard,
      reportDateRange: dashboardReportsStore.reportDateRange,
      contextDateRange: dashboardReportsStore.contextDateRange,
      reports: dashboardsStore.reports,
      industries: dashboardsStore.industries,
      brands: dashboardsStore.dashboardIdentityBrands,
      topics,
      comparisons,
    });

    mixpanelTracker.dashboardPDFDownload({
      payload: {
        ...dashboardTrackingProps.value,
        type: capitalize(type),
        fileName: filename,
        reports: reportsIdAndType,
        format: 'PDF',
      },
    });
  }

  async function downloadDashboardSpreadsheet({
    socketId,
    mixpanelPayload = {},
  }: DownloadSpreadsheetOptions) {
    const dashboardsStore = useDashboardsStore();
    const dashboardReportsStore = useDashboardReportsStore();
    const trackingStore = useTrackingStore();

    const dashboardId = dashboardsStore.currentDashboardId;
    const startDate = dashboardReportsStore.reportDateRangeInUserTimezone?.[0];
    const endDate = dashboardReportsStore.reportDateRangeInUserTimezone?.[1];
    const contextStartDate = dashboardReportsStore.contextDateRangeInUserTimezone?.[0];
    const contextEndDate = dashboardReportsStore.contextDateRangeInUserTimezone?.[1];
    const formatType = 'xlsx';
    const type = 'dashboard';
    const timeScale = dashboardReportsStore.graphScale;
    const reports = dashboardsStore.reports;

    const reportsIdAndType = Object.entries(reports).map((report) => ({
      report_id: (report[1] as Report).id,
      report_type: (report[1] as Report).type,
    }));

    const filterOverrides = buildFilterOverrideParams({
      contentTagIds: dashboardReportsStore.contentTagIds,
      contentTagMatchType: dashboardReportsStore.contentTagMatchType,
      messageTagIds: dashboardReportsStore.messageTagIds,
      messageTagMatchType: dashboardReportsStore.messageTagMatchType,
      brandIds: dashboardReportsStore.brandIds,
      brandTagIds: dashboardReportsStore.brandTagIds,
      competitorSourceAccountIds: dashboardReportsStore.competitorSourceAccountIds,
      competitorTagIds: dashboardReportsStore.competitorTagIds,
      campaignIds: dashboardReportsStore.campaignIds,
      boardIds: dashboardReportsStore.boardIds,
    });

    const response = await DashboardAPI.getDashboardSpreadsheet({
      dashboardId,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      format: formatType,
      socketId,
      timeScale,
      filterOverrides,
    });

    if (response.status === 202) {
      setDownloadingSpreadsheetExport(true);
    }

    const { dashboardTrackingProps } = useDashboardTrackingProps({
      dashboard: dashboardsStore.currentDashboard,
      reportDateRange: dashboardReportsStore.reportDateRangeInUserTimezone,
      contextDateRange: dashboardReportsStore.contextDateRangeInUserTimezone,
      reports: dashboardsStore.reports,
      industries: dashboardsStore.industries,
      brands: dashboardsStore.dashboardIdentityBrands,
      topics: mixpanelPayload?.topics || [],
      comparisons: mixpanelPayload?.comparisons || [],
    });

    trackingStore.track('Report Downloaded', {
      ...dashboardTrackingProps.value,
      type: capitalize(type),
      reports: reportsIdAndType,
      format: 'XLSX',
    });
  }

  async function downloadReportCSV({ report, messageTagFilterOptions }: DownloadReportCSVOptions) {
    const dashboardsStore = useDashboardsStore();
    const dashboardReportsStore = useDashboardReportsStore();

    const reportId = report?.id;
    const dashboardId = dashboardsStore.currentDashboardId;
    const startDate = dashboardReportsStore.reportDateRangeInUserTimezone?.[0];
    const endDate = dashboardReportsStore.reportDateRangeInUserTimezone?.[1];
    const contextStartDate = dashboardReportsStore.contextDateRangeInUserTimezone?.[0];
    const contextEndDate = dashboardReportsStore.contextDateRangeInUserTimezone?.[1];
    const timeScale = dashboardReportsStore.graphScale;

    const filterOverrides = buildFilterOverrideParams({
      contentTagIds: dashboardReportsStore.contentTagIds,
      contentTagMatchType: dashboardReportsStore.contentTagMatchType,
      messageTagIds: dashboardReportsStore.messageTagIds,
      messageTagMatchType: dashboardReportsStore.messageTagMatchType,
      brandIds: dashboardReportsStore.brandIds,
      brandTagIds: dashboardReportsStore.brandTagIds,
      competitorSourceAccountIds: dashboardReportsStore.competitorSourceAccountIds,
      competitorTagIds: dashboardReportsStore.competitorTagIds,
      campaignIds: dashboardReportsStore.campaignIds,
      boardIds: dashboardReportsStore.boardIds,
    });

    const response = await DashboardAPI.getReportCsv({
      dashboardId,
      reportId,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      timeScale,
      filterOverrides,
    });

    const fileName = response?.headers?.['x-filename'] || 'dashboard-report.csv';
    downloadFileFromMemory(response?.data, fileName);

    const meta = report?.meta;

    mixpanelTracker.dashboardReportCSVDownload({
      payload: {
        fileName,
        reportId,
        meta,
        reportType: report?.type,
        format: 'CSV',
        dashboardStartDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[0],
        dashboardEndDate: dashboardReportsStore.reportDateRangeISOStringInUserTimezone?.[1],
        dashboardContextStartDate:
          dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[0],
        dashboardContextEndDate: dashboardReportsStore.contextDateRangeISOStringInUserTimezone?.[1],
      },
      dashboardProperties: {
        dashboardIdentityBrands: dashboardsStore.dashboardIdentityBrands,
        currentDashboardId: dashboardsStore.currentDashboardId,
        currentDashboardName: dashboardsStore.currentDashboardName,
        dashboardCustomizeMode: dashboardsStore.dashboardCustomizeMode,
        industries: dashboardsStore.industries,
      },
      messageTagFilterOptions,
    });
  }

  async function downloadReportDataCSV({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    reportType,
    sortOrder,
    limit,
    timeScale,
    filenamePrefix,
    csvLayout,
  }: DownloadReportDataCSVOptions) {
    setReportDataCsvLoading(true);
    const response = await DashboardAPI.getReportDataCsv({
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      channels,
      metrics,
      reportType,
      sortOrder,
      limit,
      timeScale,
      filenamePrefix,
      csvLayout,
    });
    const fileName = response?.headers?.['x-filename'] || 'dashboard-report.csv';
    downloadFileFromMemory(response?.data, fileName);
    setReportDataCsvLoading(false);
  }

  // -------------------------------------------------------------------------
  // Actions - Auto Export CRUD
  // -------------------------------------------------------------------------

  async function getAllDashboardAutoExports() {
    const response = await DashboardAPI.getAllDashboardAutomatedExports();
    const autoExports = response?.data as unknown as AutoExport[];
    updateAllDashboardAutoExports(autoExports);
  }

  async function refreshDashboardAutoExports() {
    const dashboardsStore = useDashboardsStore();

    if (allDashboardAutoExports.value?.length > 0) {
      void getAllDashboardAutoExports();
    }
    const dashboardId = dashboardsStore.currentDashboardId;
    if (dashboardId) {
      const response = await DashboardAPI.getDashboardAutomatedExports({ dashboardId });
      const autoExports = response?.data as unknown as AutoExport[];
      updateCurrentDashboardAutoExports(autoExports);
    }
  }

  async function createAutoExport({
    dashboard,
    autoExport,
  }: {
    dashboard: Dashboard;
    autoExport: AutoExport;
  }) {
    await DashboardAPI.createDashboardAutomatedExports({
      dashboardId: dashboard?.id,
      autoExport: autoExport as unknown as Parameters<
        typeof DashboardAPI.createDashboardAutomatedExports
      >[0]['autoExport'],
    });
    mixpanelTracker.createAutoExport({
      payload: {
        dashboard,
        ...autoExport,
      },
    });

    await refreshDashboardAutoExports();
  }

  async function updateAutoExport({
    dashboard,
    autoExport,
  }: {
    dashboard: Dashboard;
    autoExport: AutoExport;
  }) {
    await DashboardAPI.updateDashboardAutomatedExports({
      dashboardId: dashboard?.id,
      autoExportId: autoExport?.id,
      autoExport: autoExport as unknown as Parameters<
        typeof DashboardAPI.updateDashboardAutomatedExports
      >[0]['autoExport'],
    });
    const previousAutoExport = autoExportFormTarget.value;
    mixpanelTracker.editAutoExport({
      payload: {
        dashboard,
        ...autoExport,
      },
      previousPayload: {
        ...previousAutoExport,
      },
    });

    await refreshDashboardAutoExports();
  }

  async function unsubscribeAutoExport({
    dashboard,
    autoExport,
    userId,
  }: {
    dashboard: Dashboard;
    autoExport: AutoExport;
    userId: number;
  }) {
    const trackingStore = useTrackingStore();

    await DashboardAPI.deleteDashboardAutomatedExportUser({
      dashboardId: dashboard?.id,
      autoExportId: autoExport?.id,
      userId,
    });
    trackingStore.track('Dashboard Unsubscribe Auto Export', {
      dashboardId: dashboard?.id,
      dashboardName: dashboard?.name,
      autoExportId: autoExport?.id,
      recipientUserIdRemoved: userId,
    });
    await refreshDashboardAutoExports();
  }

  async function removeAutomatedExport({
    dashboard,
    autoExport,
  }: {
    dashboard: Dashboard;
    autoExport: AutoExport;
  }) {
    await DashboardAPI.deleteAutomatedExport({
      dashboardId: dashboard?.id,
      autoExportId: autoExport?.id,
    });
    mixpanelTracker.deleteAutoExport({
      payload: {
        ...dashboard,
        ...autoExport,
      },
    });
    await refreshDashboardAutoExports();
  }

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // State
    _currentDashboardAutoExports,
    allDashboardAutoExports,
    autoExportFormShow,
    autoExportFormMode,
    autoExportFormTarget,
    autoExportListShow,
    spreadsheetExportDownloading,
    reportDataCsvLoading,

    // Getters
    currentDashboardAutoExports,

    // Actions - Form Management
    launchScheduleAutoExportForm,
    closeScheduleAutoExportForm,
    launchAutoExportList,
    closeAutoExportList,

    // Actions - State Updates
    setDownloadingSpreadsheetExport,
    updateAllDashboardAutoExports,
    updateCurrentDashboardAutoExports,
    clearCurrentDashboardAutoExports,
    setReportDataCsvLoading,
    // Alias for backwards compatibility
    downloadingReportDataCsv: setReportDataCsvLoading,

    // Actions - Downloads
    downloadDashboardPDF,
    downloadDashboardSpreadsheet,
    downloadReportCSV,
    downloadReportDataCSV,

    // Actions - Auto Export CRUD
    getAllDashboardAutoExports,
    refreshDashboardAutoExports,
    createAutoExport,
    updateAutoExport,
    unsubscribeAutoExport,
    removeAutomatedExport,
  };
});
